import React, { useCallback, useMemo, useState } from 'react';
import ReactSelect, { SelectOption } from '@components/atoms/ReactSelect';
import { SingleValue, MultiValue } from 'react-select';
import { Loading } from '@components/atoms/Loading';
import { SearchInput } from '@components/molecules/SearchInput';
import WorldMap, {
  WorldMapProjectMarker,
} from '@components/organisms/WorldMap';
import UsersInsideAreaDrawer from '@components/organisms/UsersInsideAreaDrawer';
import { useProjectsByState } from '@hooks/api/projects/useProjectsByState';
import { useWarehouses } from '@hooks/api/warehouse/useWarehouses';
import { useGeofences } from '@hooks/api/geofence';
import { Geofence, GeofenceShapeType } from '@models/geofence.model';
import { UsersInsideAreaFilter } from '@models/usersInsideArea.model';
import { Project } from '@models/project.model';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';

const ALL_OPTION_VALUE = '__all__';

interface DrawerState {
  filter: UsersInsideAreaFilter;
  areaName: string;
  areaSubtitle: string;
  relatedProjectId?: string | null;
  relatedWarehouseId?: string | null;
  lat?: number | null;
  lng?: number | null;
}

const MapViewPage: React.FC = () => {
  const [showOngoing, setShowOngoing] = useState(true);
  const [showCompleted, setShowCompleted] = useState(false);
  const [showAbandoned, setShowAbandoned] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [filterProjectId, setFilterProjectId] = useState<string>('');
  const [filterWarehouseId, setFilterWarehouseId] = useState<string>('');

  const [showProjects, setShowProjects] = useState(true);
  const [showGeofences, setShowGeofences] = useState(true);

  const [drawer, setDrawer] = useState<DrawerState | null>(null);

  // Data
  const states = useMemo(() => {
    const list: string[] = [];
    if (showOngoing) list.push('ongoing');
    if (showCompleted) list.push('completed');
    if (showAbandoned) list.push('abandoned');
    return list;
  }, [showOngoing, showCompleted, showAbandoned]);

  const { data: projects = [], isLoading: projectsLoading } = useProjectsByState(states);
  const { data: warehouses = [] } = useWarehouses();
  const { data: geofences = [], isLoading: geofencesLoading } = useGeofences({
    isActive: true,
  });

  // Apply filters to projects
  const filteredProjects = useMemo(() => {
    let list = (projects as Project[]).filter(
      (p) => typeof p.lat === 'number' && typeof p.long === 'number',
    );

    if (filterProjectId) {
      list = list.filter((p) => p.projectId === filterProjectId);
    }
    if (filterWarehouseId) {
      // When filtering by warehouse, hide all project markers (warehouse-only view)
      list = [];
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (p) =>
          (p.name && p.name.toLowerCase().includes(q)) ||
          (p.address && p.address.toLowerCase().includes(q)),
      );
    }
    return list;
  }, [projects, filterProjectId, filterWarehouseId, searchQuery]);

  // Apply filters to geofences. Defensive: even though the server query already
  // filters by isActive, we re-check on the client so a stale cache slice never
  // leaks an inactive geofence onto the map between toggle + refetch.
  const filteredGeofences = useMemo(() => {
    let list = geofences.filter((g) => g.isActive);
    if (filterProjectId) {
      list = list.filter((g) => g.projectId === filterProjectId);
    }
    if (filterWarehouseId) {
      list = list.filter((g) => g.warehouseId === filterWarehouseId);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (g) =>
          g.name.toLowerCase().includes(q) ||
          g.category?.name.toLowerCase().includes(q) ||
          g.project?.name.toLowerCase().includes(q) ||
          g.warehouse?.name.toLowerCase().includes(q),
      );
    }
    return list;
  }, [geofences, filterProjectId, filterWarehouseId, searchQuery]);

  // Project markers + the set of project IDs that already have a geofence
  const projectMarkers = useMemo<WorldMapProjectMarker[]>(
    () =>
      filteredProjects.map((p) => ({
        projectId: p.projectId as string,
        name: p.name || 'No name',
        lat: p.lat as number,
        lng: p.long as number,
      })),
    [filteredProjects],
  );

  // Only hide the project marker when there's a POLYGON geofence — polygons
  // already draw a clear area on the map. POINT_RADIUS geofences (typically the
  // 100m default) are kept alongside the marker so the project is visible at
  // any zoom level.
  const projectIdsWithGeofence = useMemo(() => {
    const ids = new Set<string>();
    filteredGeofences.forEach((g) => {
      if (g.projectId && g.shape.type === GeofenceShapeType.POLYGON) {
        ids.add(g.projectId);
      }
    });
    return ids;
  }, [filteredGeofences]);

  // Filter dropdown options
  const projectOptions = useMemo<SelectOption[]>(
    () => [
      { value: ALL_OPTION_VALUE, label: 'All projects' },
      ...(projects as Project[]).map((p) => ({
        value: p.projectId as string,
        label: p.name || 'No name',
      })),
    ],
    [projects],
  );

  const warehouseOptions = useMemo<SelectOption[]>(
    () => [
      { value: ALL_OPTION_VALUE, label: 'All warehouses' },
      ...warehouses.map((w: any) => ({ value: w.warehouseId, label: w.name })),
    ],
    [warehouses],
  );

  const noProjects = projectOptions.length === 1; // only "All projects"
  const noWarehouses = warehouseOptions.length === 1;

  const selectedProjectOption =
    (filterProjectId
      ? projectOptions.find((o) => o.value === filterProjectId)
      : projectOptions[0]) || null;
  const selectedWarehouseOption =
    (filterWarehouseId
      ? warehouseOptions.find((o) => o.value === filterWarehouseId)
      : warehouseOptions[0]) || null;

  // Handlers
  const handleProjectFilterChange = (
    opt: SingleValue<SelectOption> | MultiValue<SelectOption> | null,
  ) => {
    if (!opt || Array.isArray(opt) || !('value' in opt)) {
      setFilterProjectId('');
      return;
    }
    setFilterProjectId(opt.value === ALL_OPTION_VALUE ? '' : (opt.value as string));
  };
  const handleWarehouseFilterChange = (
    opt: SingleValue<SelectOption> | MultiValue<SelectOption> | null,
  ) => {
    if (!opt || Array.isArray(opt) || !('value' in opt)) {
      setFilterWarehouseId('');
      return;
    }
    setFilterWarehouseId(
      opt.value === ALL_OPTION_VALUE ? '' : (opt.value as string),
    );
  };

  const handleStatusFilterChange = (
    type: 'ongoing' | 'completed' | 'abandoned',
    checked: boolean,
  ) => {
    if (type === 'ongoing') setShowOngoing(checked);
    if (type === 'completed') setShowCompleted(checked);
    if (type === 'abandoned') setShowAbandoned(checked);
  };

  const handleSelectProject = useCallback((project: WorldMapProjectMarker) => {
    setDrawer({
      filter: { projectId: project.projectId },
      areaName: project.name,
      areaSubtitle: 'Project',
      relatedProjectId: project.projectId,
      lat: project.lat,
      lng: project.lng,
    });
  }, []);

  const handleSelectGeofence = useCallback((geofence: Geofence) => {
    // Prefer projectId-scoped query when the geofence is linked to a project,
    // since legacy mobile builds only send projectId. When geofenceId becomes
    // populated reliably, we can flip this to always use geofenceId.
    const filter: UsersInsideAreaFilter = geofence.projectId
      ? { projectId: geofence.projectId }
      : { geofenceId: geofence.geofenceId };

    const subtitle = [
      geofence.category?.name,
      geofence.project?.name && `Project: ${geofence.project.name}`,
      geofence.warehouse?.name && `Warehouse: ${geofence.warehouse.name}`,
    ]
      .filter(Boolean)
      .join(' · ');

    // Pick a navigable point for the directions link — circle center or
    // polygon centroid.
    const center =
      geofence.shape.center ??
      (geofence.shape.polygonCoordinates &&
      geofence.shape.polygonCoordinates.length > 0
        ? geofence.shape.polygonCoordinates.reduce(
            (acc, p) => ({
              lat: acc.lat + p.lat / geofence.shape.polygonCoordinates!.length,
              lng: acc.lng + p.lng / geofence.shape.polygonCoordinates!.length,
            }),
            { lat: 0, lng: 0 },
          )
        : null);

    setDrawer({
      filter,
      areaName: geofence.name,
      areaSubtitle: subtitle || 'Geofence',
      relatedProjectId: geofence.projectId,
      relatedWarehouseId: geofence.warehouseId,
      lat: center?.lat ?? null,
      lng: center?.lng ?? null,
    });
  }, []);

  const closeDrawer = useCallback(() => setDrawer(null), []);

  const isLoading = projectsLoading || geofencesLoading;

  return (
    <div className="flex flex-col w-full h-full">
      {/* Header */}
      <div className="flex items-center gap-2 pt-6 pl-10 pr-6 mb-3">
        <FilterAltOutlinedIcon
          sx={{ fontSize: 28 }}
          className="text-orange-400 dark:text-orange-300"
        />
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Map View</h1>
      </div>

      {/* Unified filters */}
      <div className="pl-10 pr-6 space-y-3">
        {/* Row 1: Search + project + warehouse */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <SearchInput
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search projects or geofences..."
            className="w-full"
          />
          <ReactSelect
            options={projectOptions}
            value={noProjects ? null : selectedProjectOption}
            onChange={handleProjectFilterChange}
            placeholder={noProjects ? 'No projects available' : 'Filter by project'}
            isDisabled={noProjects}
          />
          <ReactSelect
            options={warehouseOptions}
            value={noWarehouses ? null : selectedWarehouseOption}
            onChange={handleWarehouseFilterChange}
            placeholder={
              noWarehouses ? 'No warehouses available' : 'Filter by warehouse'
            }
            isDisabled={noWarehouses}
          />
        </div>

        {/* Row 2: layer toggles + status checkboxes (all inline, no Card) */}
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-gray-700 bg-white rounded-lg border border-gray-200 px-4 py-2">
          <span className="inline-flex items-center gap-1.5 text-gray-500 font-medium">
            <LayersOutlinedIcon sx={{ fontSize: 16 }} />
            Layers:
          </span>
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showProjects}
              onChange={(e) => setShowProjects(e.target.checked)}
              className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
            />
            <span>Projects</span>
          </label>
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showGeofences}
              onChange={(e) => setShowGeofences(e.target.checked)}
              className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
            />
            <span>Geofences</span>
          </label>

          <span className="hidden md:inline-block w-px h-5 bg-gray-200" />

          <span className="inline-flex items-center gap-1.5 text-gray-500 font-medium">
            <FilterAltOutlinedIcon sx={{ fontSize: 16 }} />
            Status:
          </span>
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showOngoing}
              onChange={(e) => handleStatusFilterChange('ongoing', e.target.checked)}
              className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
            />
            <span>Active</span>
          </label>
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showCompleted}
              onChange={(e) => handleStatusFilterChange('completed', e.target.checked)}
              className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
            />
            <span>Completed</span>
          </label>
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showAbandoned}
              onChange={(e) => handleStatusFilterChange('abandoned', e.target.checked)}
              className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
            />
            <span>Abandoned</span>
          </label>
        </div>
      </div>

      {/* Map */}
      <div className="flex-1 pl-10 pr-6 pt-3 pb-6 min-h-0">
        <div className="h-full rounded-lg overflow-hidden shadow-lg border border-gray-200 bg-gray-50 relative">
          {isLoading && (
            <div className="absolute top-2 right-2 z-10 bg-white rounded-md shadow px-2 py-1">
              <Loading size="sm" />
            </div>
          )}
          <WorldMap
            projects={projectMarkers}
            geofences={filteredGeofences}
            projectIdsWithGeofence={projectIdsWithGeofence}
            showProjects={showProjects}
            showGeofences={showGeofences}
            onSelectProject={handleSelectProject}
            onSelectGeofence={handleSelectGeofence}
          />
        </div>
      </div>

      <UsersInsideAreaDrawer
        isOpen={!!drawer}
        onClose={closeDrawer}
        filter={drawer?.filter ?? null}
        areaName={drawer?.areaName}
        areaSubtitle={drawer?.areaSubtitle}
        relatedProjectId={drawer?.relatedProjectId}
        relatedWarehouseId={drawer?.relatedWarehouseId}
        lat={drawer?.lat ?? null}
        lng={drawer?.lng ?? null}
      />
    </div>
  );
};

export default MapViewPage;
